import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import { FormTrainerSelectDrawer } from 'user/components/FormTrainerSelectDrawer';
import {
    IUserListItemFragment,
    IUserSelectorInput,
    UserListItemFragmentDoc,
} from 'graphql/types';
import {
    getUserSelectStatus,
    isUserAdded,
    isUserSelected,
} from 'user/utils/userSelect';
import { UserListItem } from 'user/components/UserListItem/UserListItem';
import { AddButton } from 'common/components/Button/AddButton';
import { asTypedControl } from 'common/utils/form';
import { TControllerProps } from 'common/types';

interface IProps {
    name: string;
    disabledTrainerIds?: string[];
    isEditable?: boolean;
    label?: string;
    noResultsLabel?: string;
    selectorQueryVariables?: IUserSelectorInput;
}

export const FormTrainerSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    disabledTrainerIds,
    isEditable = true,
    label,
    name,
    noResultsLabel,
    selectorQueryVariables,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedTrainers, setSelectedTrainers] = useState<
        IUserListItemFragment[]
    >([]);
    const client = useApolloClient();

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedTrainerIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedTrainers = useMemo(() => {
        // Get trainer objects from cache based on the trainer ids set in the form
        const trainers =
            addedTrainerIds
                ?.map((addedTrainerId) => {
                    try {
                        return client.cache.readFragment<IUserListItemFragment>(
                            {
                                id: `User:${addedTrainerId}`,
                                fragment: UserListItemFragmentDoc,
                                fragmentName: 'UserListItem',
                            }
                        );
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return trainers as IUserListItemFragment[];
    }, [addedTrainerIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedTrainers([]);

        setDrawerOpen(open);
    };

    const handleSelectTrainer = (trainer: IUserListItemFragment) => {
        setSelectedTrainers([...selectedTrainers, trainer]);
    };

    const handleRemoveTrainer = (trainer: IUserListItemFragment) => {
        const { isSelected } = getUserSelectStatus(
            selectedTrainers,
            addedTrainers,
            trainer
        );

        if (isSelected) {
            const newSelectedTrainers = selectedTrainers.filter(
                (selectedTrainer) => selectedTrainer.id !== trainer.id
            );

            setSelectedTrainers(newSelectedTrainers);

            return;
        }

        setValue(
            name,
            addedTrainers
                .filter((addedTrainer) => addedTrainer.id !== trainer.id)
                .map((trainer) => trainer.id),
            { shouldDirty: true }
        );
    };

    const handleAddTrainers = () => {
        setValue(
            name,
            [
                ...(addedTrainerIds || []),
                ...selectedTrainers.map(
                    (selectedTrainer) => selectedTrainer.id
                ),
            ],
            { shouldDirty: true }
        );

        setSelectedTrainers([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {isEditable && (
                    <AddButton
                        aria-label={label}
                        error={
                            !!error?.message && isTouched
                                ? error.message
                                : undefined
                        }
                        onClick={() => handleDrawerOpen(true)}
                    >
                        {label || translate('addTrainersTitle')}
                    </AddButton>
                )}

                {!!addedTrainers.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: isEditable ? 2 : 0,
                        }}
                    >
                        {addedTrainers.map(
                            (addedTrainer: IUserListItemFragment) => (
                                <UserListItem
                                    addButton={isEditable}
                                    added={isUserAdded(
                                        addedTrainers,
                                        addedTrainer
                                    )}
                                    key={`added-${addedTrainer.id}`}
                                    selected={isUserSelected(
                                        selectedTrainers,
                                        addedTrainer
                                    )}
                                    user={addedTrainer}
                                    onClick={(
                                        trainer: IUserListItemFragment
                                    ) => {
                                        if (!isEditable) return;

                                        handleRemoveTrainer(trainer);
                                    }}
                                />
                            )
                        )}
                    </Box>
                )}

                {!isEditable && !addedTrainers.length && (
                    <Box>
                        <Typography>
                            {noResultsLabel ||
                                translate('noOptionsText.trainers')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <FormTrainerSelectDrawer
                addedTrainers={addedTrainers}
                disabledTrainerIds={disabledTrainerIds}
                open={drawerOpen}
                selectedTrainers={selectedTrainers}
                selectorQueryVariables={selectorQueryVariables}
                onAddTrainers={handleAddTrainers}
                onDrawerOpen={handleDrawerOpen}
                onRemoveTrainer={handleRemoveTrainer}
                onSelectTrainer={handleSelectTrainer}
            />
        </Box>
    );
};
